import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions as MuiCardActions,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const StudentDocumentDialog = ({ open, handleClose, studentId, studentName }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (open && studentId) {
      fetchDocumentTypes();
      fetchStudentDocuments();
    }
  }, [open, studentId]);

  // Fetch Active Document Types
  const fetchDocumentTypes = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/get-active-document-types`);
      if (response.data.status) {
        setDocumentTypes(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load document types');
    }
  };

  // Fetch Student Uploaded Documents
  const fetchStudentDocuments = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/get-student-documents`, {
        student_id: studentId,
      });
      if (response.data.status) {
        setDocuments(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to load uploaded documents');
    } finally {
      setLoading(false);
    }
  };

  // File Change & Validation
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    const maxSizeInMB = 10;

    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file format. Only JPG, PNG, WEBP, and PDF are allowed.');
      e.target.value = '';
      setSelectedFile(null);
      return;
    }

    if (file.size > maxSizeInMB * 1024 * 1024) {
      toast.error('File size exceeds the 10MB limit.');
      e.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  // Upload Document
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!selectedDocType) {
      toast.error('Please select a document type');
      return;
    }

    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('student_id', studentId);
    formData.append('document_type_id', selectedDocType);
    formData.append('file', selectedFile);

    setUploading(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/upload-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status) {
        toast.success(response.data.message || 'Document uploaded successfully!');
        setSelectedDocType('');
        setSelectedFile(null);
        // Reset file input element
        document.getElementById('document-file-input').value = '';
        fetchStudentDocuments();
      } else {
        toast.error(response.data.message || 'Upload failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error uploading document';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Delete Document
  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setDeletingId(docId);
    try {
      const response = await axios.delete(`${BASE_URL}/api/destroy-documents/${docId}`);
      toast.success(response.data.message || 'Document deleted successfully!');
      fetchStudentDocuments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" fontWeight="bold">
          Document Management {studentName ? `- ${studentName}` : ''}
        </Typography>
        <IconButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Upload Form Section */}
        <Box
          component="form"
          onSubmit={handleUpload}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="subtitle1" fontWeight="600" mb={2}>
            Upload New Document
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel id="doc-type-label">Select Document Type</InputLabel>
                <Select
                  labelId="doc-type-label"
                  value={selectedDocType}
                  label="Select Document Type"
                  onChange={(e) => setSelectedDocType(e.target.value)}
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                size="medium"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: 'none', height: '40px' }}
              >
                {selectedFile ? selectedFile.name.substring(0, 15) + '...' : 'Choose File'}
                <input
                  id="document-file-input"
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  onChange={handleFileChange}
                />
              </Button>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={uploading}
                sx={{ height: '40px' }}
              >
                {uploading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Uploaded Documents List */}
        <Typography variant="subtitle1" fontWeight="600" mb={2}>
          Uploaded Documents
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            No documents uploaded yet.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {documents.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: '0.3s',
                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                  }}
                >
                  {doc.type === 'image' ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={doc.image_path}
                      alt={doc.document_type_name}
                      sx={{ objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => window.open(doc.image_path, '_blank')}
                    />
                  ) : (
                    <Box
                      height="140px"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      bgcolor="#f0f4f8"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => window.open(doc.image_path, '_blank')}
                    >
                      <PdfIcon sx={{ fontSize: 50, color: '#d32f2f' }} />
                      <Typography variant="caption" color="text.secondary" mt={1}>
                        Click to preview PDF
                      </Typography>
                    </Box>
                  )}

                  <CardContent sx={{ pb: 1, pt: 1.5 }}>
                    <Typography variant="subtitle2" noWrap fontWeight="bold">
                      {doc.document_type_name}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                      <Chip
                        label={doc.type.toUpperCase()}
                        size="small"
                        color={doc.type === 'pdf' ? 'error' : 'primary'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>

                  <MuiCardActions sx={{ justifyContent: 'space-between', px: 1.5, pb: 1 }}>
                    <Tooltip title="View Document">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => window.open(doc.image_path, '_blank')}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Document">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === doc.id}
                        onClick={() => handleDelete(doc.id)}
                      >
                        {deletingId === doc.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </MuiCardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentDocumentDialog;